/**
 * Main Search Template JavaScript
 * Handles search functionality with proper card product integration
 * 
 * @requires ThemeUtils (utils.js)
 */

class MainSearch {
  constructor() {
    this.searchForm = document.querySelector('.search-form');
    this.searchInput = document.querySelector('.search-input');
    this.searchResultsSection = document.querySelector('.search-results-section');
    this.searchResultsGrid = null;
    this.searchNoResults = document.querySelector('.search-no-results');
    this.searchLoading = document.querySelector('.search-loading');
    
    // Search state
    this.currentQuery = '';
    this.currentController = null;
    this.isSearching = false;
    
    this.init();
  }

  init() {
    // Only initialize if search elements exist
    if (!this.searchForm && !this.searchInput) return;

    // Save the server-rendered (Liquid-translated) header text so clearResults can restore it
    this._originalCaption = document.querySelector('.search-caption')?.textContent || '';
    this._originalTitle = document.querySelector('.search-title')?.textContent || '';
    this._originalCount = document.querySelector('.search-results-count')?.textContent || '';

    this.bindEvents();
    this.handleInitialState();
  }

  bindEvents() {
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', this.handleSubmit.bind(this));
    }

    if (this.searchInput) {
      // Use ThemeUtils.debounce if available, fallback to internal
      const debounce = window.ThemeUtils?.debounce || this.debounce;
      this.searchInput.addEventListener('input', debounce(this.handleInput.bind(this), 300));
      this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
    }
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState.bind(this));
  }

  handleSubmit(event) {
    event.preventDefault();
    const query = this.searchInput?.value.trim();
    if (query) {
      this.performSearch(query);
    }
  }

  handleInput(event) {
    const query = event.target.value.trim();
    
    if (!query) {
      if (!this.isSearching && this.currentQuery !== query) {
        this.clearResults();
      }
      return;
    }
    
    if (query !== this.currentQuery && query.length >= 2) {
      this.performSearch(query);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.searchInput?.blur();
      this.clearResults();
    }
  }

  async performSearch(query) {
    this.isSearching = true;
    
    // Cancel previous search
    if (this.currentController) {
      this.currentController.abort();
    }
    
    this.currentController = new AbortController();
    
    try {
      this.showLoading();
      
      const response = await fetch(`/search?q=${encodeURIComponent(query)}&type=product&view=json`, {
        signal: this.currentController.signal
      });
      
      if (!response.ok) {
        throw new Error(`Search failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      const products = data.results || data.products || [];
      
      this.currentQuery = query;
      this.displayResults(products, query);
      this.updateURL(query);
      this.updateURL(query);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.showError('Search failed. Please try again.');
      }
    } finally {
      this.hideLoading();
      this.currentController = null;
      this.isSearching = false; // Clear searching flag
    }
  }

  displayResults(products, query) {
    this.updateHeader(query, products.length);
    
    if (products.length === 0) {
      this.showNoResults(query);
    } else {
      this.showResults(products);
    }
    
    // Show results section
    if (this.searchResultsSection) {
      this.searchResultsSection.style.display = 'block';
    }
  }

  async showResults(products) {
    // Find the correct grid to use
    this.findOrCreateGrid();
    
    if (!this.searchResultsGrid) {
      return;
    }
    
    // Hide loading and no results
    this.hideLoading();
    
    if (this.searchNoResults) {
      this.searchNoResults.style.display = 'none';
    }
    
    // Clear grid
    this.searchResultsGrid.innerHTML = '';
    
    // Create all cards first (in parallel)
    const cardPromises = products.map(product => this.createCard(product));
    
    try {
      // Wait for all cards to be created
      const cards = await Promise.all(cardPromises);
      
      // Add all cards to the grid at once
      cards.forEach(card => {
        this.searchResultsGrid.appendChild(card);
      });
      
    } catch (error) {
      // Fallback: try to create cards one by one
      for (const product of products) {
        try {
          const card = await this.createCard(product);
          this.searchResultsGrid.appendChild(card);
        } catch (error) {
          // Silent fail for individual cards
        }
      }
    }
    
    // Show grid and initialize card functionality
    this.searchResultsGrid.style.display = 'grid';
    this.searchResultsGrid.style.opacity = '1';
    this.searchResultsGrid.style.visibility = 'visible';
    
    // Remove loading state immediately
    this.searchResultsGrid.classList.remove('search-results-grid--loading');
    
    // Initialize card products after a brief delay
    setTimeout(() => {
      if (window.CardProduct) {
        window.CardProduct.initializeAll();
      }
      
      // Preserve the search input value after showing results
      if (this.searchInput && this.currentQuery) {
        this.searchInput.value = this.currentQuery;
      }
      
      // Double-check that grid is still visible
      if (this.searchResultsGrid) {
        // Force visibility if needed
        if (this.searchResultsGrid.style.display !== 'grid') {
          this.searchResultsGrid.style.display = 'grid';
          this.searchResultsGrid.style.opacity = '1';
          this.searchResultsGrid.style.visibility = 'visible';
        }
      }
    }, 100);
  }

  async createCard(product) {
    // Try to use the server-rendered snippet first
    try {
      const handle = product.url.split('/').pop().split('?')[0];
      const showDesc = this.getShowDescription();
      
      const cardUrl = `/products/${handle}?view=card&show_description=${showDesc}`;
      
      const response = await fetch(cardUrl);
      
      if (response.ok) {
        const html = await response.text();
        
        if (html.trim().length > 50) {
          const wrapper = document.createElement('div');
          wrapper.className = 'product-card-wrapper';
          wrapper.innerHTML = html.trim();
          return wrapper;
        }
      }
    } catch (error) {
      // Silent fail, use fallback
    }
    
    // Fallback to simple card
    return this.createSimpleCard(product);
  }

  createSimpleCard(product) {
    const wrapper = document.createElement('div');
    wrapper.className = 'product-card-wrapper';
    
    const price = product.price ? (product.price / 100).toFixed(2) : '0.00';
    
    const cardHTML = `
      <div class="card-product" data-product-id="${product.id}">
        <div class="card-product__media-wrapper">
          <a href="${product.url}" class="card-product__media-link">
            ${product.featured_image ? 
              `<img src="${product.featured_image}&width=400" alt="${product.title}" loading="lazy" class="card-product__media">` :
              `<div class="card-product__no-image">No image</div>`
            }
          </a>
        </div>
        <div class="card-product__info">
          <h3 class="card-product__title heading-xs">${product.title}</h3>
          ${this.getShowDescription() && product.body ? 
            `<p class="card-product__desc body">${this.truncateText(product.body, 100)}</p>` : ''
          }
          <p class="card-product__price body">$${price}</p>
        </div>
      </div>
    `;
    
    wrapper.innerHTML = cardHTML;
    
    return wrapper;
  }

  showNoResults(query) {
    this.findOrCreateGrid();
    
    if (!this.searchNoResults) return;
    
    // Hide loading
    this.hideLoading();
    
    if (this.searchResultsGrid) {
      this.searchResultsGrid.style.display = 'none';
    }
    
    const title = this.searchNoResults.querySelector('.search-no-results-title');
    if (title) {
      const noResultsFor = window.theme?.strings?.searchNoResultsFor || 'No results found for "{{query}}"';
      title.textContent = noResultsFor.replace('{{query}}', query);
    }
    
    this.searchNoResults.style.display = 'block';
  }

  clearResults() {
    this.currentQuery = '';
    
    // Hide loading
    this.hideLoading();
    
    // Reset header
    const title = document.querySelector('.search-title');
    const caption = document.querySelector('.search-caption');
    const count = document.querySelector('.search-results-count');
    
    if (title) title.textContent = this._originalTitle || window.theme?.strings?.searchTitle || 'Search';
    if (caption) caption.textContent = this._originalCaption || window.theme?.strings?.searchEnterTerm || 'Enter a search term to find products';
    if (count) count.textContent = this._originalCount || '';
    
    // Clear all grids but DON'T hide them
    const allGrids = document.querySelectorAll('.search-results-grid');
    allGrids.forEach((grid, index) => {
      grid.innerHTML = '';
      // DON'T set display: none - let showResults handle visibility
      grid.style.opacity = '1';
      grid.style.visibility = 'visible';
      grid.classList.remove('search-results-grid--loading');
    });
    
    if (this.searchNoResults) {
      this.searchNoResults.style.display = 'none';
    }
    
  // DON'T hide the results section or mutate history here; keep state stable
  }

  updateHeader(query, count) {
    const title = document.querySelector('.search-title');
    const caption = document.querySelector('.search-caption');
    const countEl = document.querySelector('.search-results-count');
    
    if (title) title.textContent = `"${query}"`;
    if (caption) caption.textContent = window.theme?.strings?.searchFor || 'Search results for';
    if (countEl) {
      const resultsLabel = window.theme?.strings?.searchResults || 'results found';
      const noResultsLabel = window.theme?.strings?.searchNoResults || 'No results found';
      countEl.textContent = count > 0 ? `${count} ${resultsLabel}` : noResultsLabel;
    }
  }

  updateURL(query) {
    const url = `/search?q=${encodeURIComponent(query)}&type=product`;
    window.history.pushState({ query }, '', url);
  }

  getShowDescription() {
    const section = document.querySelector('.search-section');
    return section?.dataset.showDescription === 'true';
  }

  truncateText(text, maxLength) {
    const stripped = text.replace(/<[^>]*>/g, '');
    return stripped.length <= maxLength ? stripped : stripped.substr(0, maxLength) + '...';
  }

  showLoading() {
    if (this.searchForm) {
      this.searchForm.classList.add('search-form--loading');
    }
    
    // Show search loading in results section
    if (this.searchLoading) {
      this.searchLoading.style.display = 'flex';
    }
    
    // Hide results grid and no-results while loading
    if (this.searchResultsGrid) {
      this.searchResultsGrid.style.display = 'none';
    }
    if (this.searchNoResults) {
      this.searchNoResults.style.display = 'none';
    }
    
    // Show results section if not visible
    if (this.searchResultsSection) {
      this.searchResultsSection.style.display = 'block';
    }
  }

  hideLoading() {
    if (this.searchForm) {
      this.searchForm.classList.remove('search-form--loading');
    }
    
    // Hide search loading
    if (this.searchLoading) {
      this.searchLoading.style.display = 'none';
    }
  }

  showError(message) {
    // Simple error handling - could be enhanced
    console.error(message);
  }

  findOrCreateGrid() {
    // Always use the first available grid for consistency
    const allGrids = document.querySelectorAll('.search-results-grid');
    
    if (allGrids.length > 0) {
      // Always use the first grid - this ensures consistency
      this.searchResultsGrid = allGrids[0];
    } else {
      // Create a new grid if none exists
      this.searchResultsGrid = document.createElement('div');
      this.searchResultsGrid.className = 'search-results-grid';
      
      const container = this.searchResultsSection?.querySelector('.container');
      if (container) {
        container.appendChild(this.searchResultsGrid);
      }
    }
  }

  handlePopState(event) {
    // Handle browser navigation (back/forward buttons)
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    
    if (query) {
      this.searchInput.value = query;
      if (query !== this.currentQuery) {
        this.performSearch(query);
      }
    } else {
      this.searchInput.value = '';
      this.clearResults();
    }
  }

  handleInitialState() {
    // Handle URL parameters on page load
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    
    if (query && this.searchInput) {
      this.searchInput.value = query;
      this.currentQuery = query;
      
      // Check if there are already products on the page (server-rendered results)
      const existingProducts = document.querySelectorAll('.search-results-grid .product-card-wrapper');
      
      if (existingProducts.length > 0) {
        // Products already exist from server render — leave the Liquid-translated text as-is
        if (this.searchResultsSection) {
          this.searchResultsSection.style.display = 'block';
        }
        
      } else {
        // No existing products, perform search (dynamic search)
        this.performSearch(query);
      }
    }
    
    // Initialize card product functionality for any existing cards on page load
    setTimeout(() => {
      if (window.CardProduct && typeof window.CardProduct.initializeAll === 'function') {
        window.CardProduct.initializeAll();
      }
      
      // Also remove loading state from any existing grids
      const allGrids = document.querySelectorAll('.search-results-grid');
      allGrids.forEach(grid => {
        grid.classList.remove('search-results-grid--loading');
        grid.style.opacity = '1';
        grid.style.visibility = 'visible';
      });
    }, 500);
  }

  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const ms = new MainSearch();
  // Safety: ensure any statically rendered grids are visible
  try {
    const resultsSection = document.querySelector('.search-results-section');
    const grids = document.querySelectorAll('.search-results-grid');
    grids.forEach((g) => {
      g.classList.remove('search-results-grid--loading');
      g.style.opacity = '1';
      g.style.visibility = 'visible';
      if (g.children.length > 0) {
        g.style.display = 'grid';
        if (resultsSection) resultsSection.style.display = 'block';
      }
    });
  } catch (e) {
    // Silent fail for visibility guard
  }
});

window.MainSearch = MainSearch;
